import { useEffect, useState } from "react";
import { Activity } from "lucide-react";
import { api } from "@/lib/api";
import { Widget } from "@/components/dashboard/Widget";
import { HealthBar } from "@/components/dashboard/HealthBar";
import { TrendPill } from "@/components/dashboard/TrendPill";

/** Career Health Command Center — 7 health bars + composite. */
export const CareerHealthWidget = ({ size = "lg" }) => {
  const [state, setState] = useState({ loading: true, data: null, error: false });

  useEffect(() => {
    let alive = true;
    api.get("/dashboard/health")
      .then((r) => { if (alive) setState({ loading: false, data: r.data, error: false }); })
      .catch(() => { if (alive) setState({ loading: false, data: null, error: true }); });
    return () => { alive = false; };
  }, []);

  const composite = state.data?.career_health;
  const compositeValue = composite?.value;
  const compositeTrend = composite?.trend;

  return (
    <Widget
      title="Career Health"
      icon={Activity}
      size={size}
      testId="career-health-widget"
    >
      {state.loading && (
        <div className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="space-y-1.5">
              <div className="flex justify-between">
                <div className="skeleton h-3 w-32" />
                <div className="skeleton h-3 w-10" />
              </div>
              <div className="skeleton h-1.5 w-full" />
            </div>
          ))}
        </div>
      )}
      {state.error && (
        <div className="text-sm text-muted-foreground text-center py-4">
          Couldn&apos;t load your health snapshot.
        </div>
      )}
      {state.data && (
        <>
          {/* Composite pill at the top */}
          <div
            className="flex items-center justify-between mb-4 pb-4 border-b border-border"
            data-testid="career-health-composite"
          >
            <div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">
                Composite score
              </div>
              <div className="flex items-baseline gap-2">
                <span className="font-display font-black text-3xl text-foreground">
                  {compositeValue == null ? "—" : compositeValue}
                </span>
                {compositeValue != null && <span className="text-sm text-muted-foreground">/ 100</span>}
              </div>
            </div>
            {compositeTrend && (
              <TrendPill
                delta={compositeTrend.delta}
                period={compositeTrend.period_days ? `${compositeTrend.period_days}d` : undefined}
                testId="career-health-composite-trend"
              />
            )}
          </div>
          <div className="space-y-3.5">
            {state.data.health_metrics.map((m) => (
              <HealthBar
                key={m.key}
                label={m.label}
                value={m.value}
                state={m.state}
                unlockAction={m.unlock_action}
                trend={m.trend}
                testId={`health-${m.key}`}
              />
            ))}
          </div>
        </>
      )}
    </Widget>
  );
};
